import { useEffect, useState } from 'react'
import {
  type ColumnDef,
  type ColumnPinningState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useIsMobile } from '@/hooks/use-mobile'
import { getColumnPinningStyles, getDataTableCellClassName } from '@/lib/data-table-pinning'
import { cn } from '@/lib/utils'

type DataTableProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  onRowClick?: (row: TData) => void
  getRowClassName?: (row: TData) => string | undefined
  emptyMessage?: string
  className?: string
  initialColumnPinning?: ColumnPinningState
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onRowClick,
  getRowClassName,
  emptyMessage = 'No results.',
  className,
  initialColumnPinning,
}: DataTableProps<TData, TValue>) {
  const isMobile = useIsMobile()
  const pinEnabled = !isMobile && Boolean(initialColumnPinning)
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(
    initialColumnPinning ?? { left: [], right: [] },
  )

  useEffect(() => {
    if (isMobile) {
      setColumnPinning({ left: [], right: [] })
      return
    }
    if (initialColumnPinning) {
      setColumnPinning(initialColumnPinning)
    }
  }, [isMobile, initialColumnPinning])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(pinEnabled
      ? {
          enableColumnPinning: true,
          onColumnPinningChange: setColumnPinning,
          state: { columnPinning },
        }
      : {
          enableColumnPinning: false,
        }),
  })

  return (
    <div className={cn('relative w-full overflow-x-auto', className)}>
      <Table noWrapper className="border-separate border-spacing-0">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              className={cn('group/row', pinEnabled && 'bg-card hover:bg-card')}
            >
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  style={pinEnabled ? getColumnPinningStyles(header.column, { isHeader: true }) : undefined}
                  className={getDataTableCellClassName(pinEnabled, header.column, { isHeader: true })}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() ? 'selected' : undefined}
                className={cn(
                  'group/row',
                  pinEnabled && 'bg-card hover:bg-transparent data-[state=selected]:bg-transparent',
                  onRowClick && 'cursor-pointer',
                  getRowClassName?.(row.original),
                )}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={pinEnabled ? getColumnPinningStyles(cell.column) : undefined}
                    className={getDataTableCellClassName(pinEnabled, cell.column)}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
